<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  authType: {
    type: String,
    default: 'none',
  },
  riskLevel: {
    type: String,
    default: 'write',
    validator: value => ['read', 'write'].includes(value),
  },
  hasAuthSecret: {
    type: Boolean,
    default: false,
  },
  enabled: {
    type: Boolean,
    default: true,
  },
  isUpdatingAvailability: {
    type: Boolean,
    default: false,
  },
  updatedAt: {
    type: Number,
    required: true,
  },
  createdAt: {
    type: Number,
    required: true,
  },
  canManage: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const menuItems = computed(() => [
  {
    label: t('CAPTAIN.CUSTOM_TOOLS.OPTIONS.EDIT_TOOL'),
    value: 'edit',
    action: 'edit',
    icon: 'i-lucide-pencil-line',
  },
  {
    label: t('CAPTAIN.CUSTOM_TOOLS.OPTIONS.DELETE_TOOL'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const timestamp = computed(() =>
  dynamicTime(props.updatedAt || props.createdAt)
);

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};

const authTypeLabel = computed(() => {
  if (props.authType === 'bearer') {
    return t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.BEARER');
  }
  if (props.authType === 'basic') {
    return t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.BASIC');
  }
  if (props.authType === 'api_key') {
    return t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.API_KEY');
  }
  return t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.NONE');
});

const riskLevelLabel = computed(() => {
  if (props.riskLevel === 'read') {
    return t('CAPTAIN.CUSTOM_TOOLS.FORM.RISK_LEVEL.OPTIONS.READ.LABEL');
  }
  return t('CAPTAIN.CUSTOM_TOOLS.FORM.RISK_LEVEL.OPTIONS.WRITE.LABEL');
});

const secretStatusLabel = computed(() => {
  if (props.hasAuthSecret) {
    return t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.SECRET_CONFIGURED');
  }
  return t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_CONFIG.SECRET_MISSING');
});

const availabilityLabel = computed(() => {
  if (props.enabled) {
    return t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.CUSTOM_ENABLED');
  }
  return t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.CUSTOM_DISABLED');
});

const availabilityActionLabel = computed(() => {
  if (props.enabled) {
    return t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_CUSTOM');
  }
  return t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.ENABLE_CUSTOM');
});
</script>

<template>
  <CardLayout class="relative">
    <div class="flex relative justify-between w-full gap-1">
      <span class="text-base text-n-slate-12 line-clamp-1 font-medium">
        {{ title }}
      </span>
      <div v-if="canManage" class="flex items-center gap-2">
        <Policy
          v-on-clickaway="() => toggleDropdown(false)"
          :permissions="['administrator']"
          class="relative flex items-center group"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            :aria-label="t('CAPTAIN.CUSTOM_TOOLS.OPTIONS.ACTIONS_MENU')"
            class="rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="menuItems"
            class="mt-1 ltr:right-0 rtl:right-0 top-full"
            @action="handleAction($event)"
          />
        </Policy>
      </div>
    </div>
    <div
      class="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
    >
      <div
        class="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
      >
        <span v-if="description" class="text-sm text-n-slate-11 sm:truncate">
          {{ description }}
        </span>
        <span
          v-if="authType !== 'none'"
          class="inline-flex min-w-0 flex-wrap items-center gap-1 text-sm text-n-slate-11 sm:shrink-0"
        >
          <i class="i-lucide-lock text-base" />
          {{ authTypeLabel }}
          <span
            class="text-xs"
            :class="hasAuthSecret ? 'text-n-teal-11' : 'text-n-ruby-11'"
          >
            {{ secretStatusLabel }}
          </span>
        </span>
      </div>
      <span
        class="shrink-0 text-xs text-n-slate-10 sm:text-sm sm:text-n-slate-11"
      >
        {{ timestamp }}
      </span>
    </div>
    <div class="flex flex-wrap items-center justify-between w-full gap-3">
      <div class="flex flex-wrap items-center gap-2">
        <span
          class="rounded-full px-2 py-1 text-xs font-medium"
          :class="
            enabled
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-amber-3 text-n-amber-11'
          "
        >
          {{ availabilityLabel }}
        </span>
        <span
          class="rounded-full px-2 py-1 text-xs font-medium"
          :class="
            riskLevel === 'read'
              ? 'bg-n-blue-3 text-n-blue-11'
              : 'bg-n-ruby-3 text-n-ruby-11'
          "
        >
          <span>{{ riskLevelLabel }}</span>
          <span v-if="riskLevel === 'write'" class="ms-1">
            {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.RISK_LEVEL.APPROVAL_REQUIRED') }}
          </span>
        </span>
      </div>
      <Button
        v-if="canManage"
        type="button"
        color="slate"
        size="xs"
        variant="faded"
        :icon="enabled ? 'i-lucide-toggle-right' : 'i-lucide-toggle-left'"
        :label="availabilityActionLabel"
        :is-loading="isUpdatingAvailability"
        :disabled="isUpdatingAvailability"
        @click="emit('action', { action: 'toggle', id })"
      />
    </div>
  </CardLayout>
</template>
